import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { marksApi, masteryTone, confidenceLabel, classEvidenceLabel } from '../lib/masteryApi';
import type { MasteryReport, MasteryStudentView, MasteryUnitView } from '../lib/masteryApi';
import { paperApi } from '../lib/questionBankApi';
import type { PaperSummary } from '../lib/questionBankApi';
import { StatTile } from '../components/charts';

/**
 * WHICH CHAPTER DOES THIS CHILD NOT UNDERSTAND.
 *
 * ⚠ THE TEACHING SIGNAL COMES FIRST, ABOVE THE CHILDREN, AND THAT ORDERING IS THE ARGUMENT.
 * When two thirds of a class are under half on one unit, the answer is not tuition for forty
 * children — it is a fortnight of lessons that did not land, and the right response is to re-teach
 * it. Every other product in this market sorts children by marks and leaves the teacher to notice
 * the pattern. Putting it at the top is the difference between a report and a diagnosis.
 *
 * ⚠ NO RANK ANYWHERE. A child is compared against the CLASS AVERAGE on a unit, never placed in an
 * order against their classmates. "34% where the class got 71%" tells a teacher what to do;
 * "22nd of 52" tells them nothing and tells the child something worse.
 *
 * ⚠ EVERY PERCENTAGE CARRIES ITS SAMPLE. A unit worth five marks of a paper yields five marks of
 * evidence about it. The screen says so on every row, because a number without its sample size is
 * how a school ends up streaming a child on noise.
 */
export function MasteryPage({ paperId, academicClassId }: {
  paperId?: string; academicClassId?: string;
}) {
  const [report, setReport] = useState<MasteryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [only, setOnly] = useState<'all' | 'behind' | 'skipped'>('all');

  useEffect(() => {
    // ⚠ NO PAPER CHOSEN IS NOT A FAILURE, and it used to render as one. The nav links here with no
    // query string, this returned early leaving `report` null, and the null branch below printed
    // "Could not load the mastery report." — a red herring for the one state where nothing has
    // gone wrong. The screen cannot guess which paper you mean, so it asks.
    if (!paperId) { setLoading(false); return; }
    let alive = true;
    marksApi
      .mastery(paperId, academicClassId)
      .then((r) => { if (alive) { setReport(r); setFailed(false); } })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [paperId, academicClassId]);

  const shown = useMemo(() => {
    if (!report) return [];
    if (only === 'behind') return report.students.filter((s) => s.weakest.length > 0);
    if (only === 'skipped') return report.students.filter((s) => s.skipped > 0);
    return report.students;
  }, [report, only]);

  if (loading) return <p style={{ color: 'var(--ink-3)' }}>Adding up the paper…</p>;
  if (!paperId) return <PaperPicker />;
  if (failed || !report) {
    return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load the mastery report.</div>;
  }

  return (
    <div className="mas">
      <header className="mas-head">
        <h1>What the paper showed</h1>
        <p className="mas-sub">
          {report.paperTitle} · {report.subjectName}
          {report.classLabel ? ` · ${report.classLabel}` : ''} · marked question by question, so the
          answer is a chapter rather than a subject.
        </p>
      </header>

      {report.note && <div className="mas-note">{report.note}</div>}

      <div className="mas-tiles">
        <StatTile label="Class average" value={`${Math.round(report.overallPercent)}%`}
          foot="of the marks attempted" />
        <StatTile label="Children" value={String(report.studentsAnalysed)} foot="marked so far" />
        <StatTile label="Needs re-teaching"
          value={String(report.teachingSignals.length)}
          foot={report.teachingSignals.length === 0
            ? 'no unit collapsed across the class'
            : <span className="tone-bad">half the class or more is under half</span>} />
        <StatTile label="Behind on a unit"
          value={String(report.students.filter((s) => s.weakest.length > 0).length)}
          foot="individually, not class-wide" />
      </div>

      {/* ---------------------------------------------------------------- teaching first */}
      {report.teachingSignals.length > 0 && (
        <div className="card mas-teaching">
          <h2>This is a teaching problem, not a pupil problem</h2>
          <p className="mas-sub">
            Half the class or more is under fifty per cent on each of these.{' '}
            {/* ⚠ The class size, not a number I typed. This read "Forty children" against a class
                of fifty-four — the one sentence on the page arguing from the size of the class. */}
            {report.studentsAnalysed} children did not all stop trying in the same fortnight —
            re-teach it before anybody sets extra work.
          </p>
          {report.teachingSignals.map((u) => (
            <div className="mas-signal" key={u.unitName}>
              <div className="mas-signal-main">
                <span className="mas-signal-name">{u.unitName}</span>
                <span className="mas-signal-sub">
                  {u.studentsBelowHalf} of {u.studentsAnalysed} children under half ·{' '}
                  {classEvidenceLabel(u.confidence, u.marksPerChild)}
                </span>
              </div>
              <span className={`mas-pct tone-${masteryTone(Math.round(u.percent))}`}>
                {Math.round(u.percent)}%
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ---------------------------------------------------------------- the class, by unit */}
      <div className="card mas-units">
        <h2>Every unit on this paper</h2>
        <table className="mas-table">
          <thead>
            <tr>
              <th>Unit</th><th className="num">Class</th><th className="num">Under half</th>
              <th>Evidence</th><th className="bar-col" />
            </tr>
          </thead>
          <tbody>
            {report.units.map((u) => {
              const pct = Math.round(u.percent);
              const tone = masteryTone(pct, u.confidence);
              const unjudged = tone === 'none';
              return (
                <tr key={u.unitName} className={unjudged ? 'is-unjudged' : ''}>
                  <td>{u.unitName}</td>
                  <td className={`num tone-${tone}`}>{pct}%</td>
                  <td className="num">{u.studentsBelowHalf}/{u.studentsAnalysed}</td>
                  <td className="muted">
                    {/* ⚠ Not both sentences. "Not enough to judge · only 4 marks per child — too
                        little to act on" says the same thing twice and pushes the row to two
                        lines; the verdict leads and the number supports it. */}
                    {unjudged ? (
                      <>
                        <strong className="mas-unjudged">Not enough to judge</strong>
                        {` · ${u.marksPerChild} ${u.marksPerChild === 1 ? 'mark' : 'marks'} per child`}
                      </>
                    ) : classEvidenceLabel(u.confidence, u.marksPerChild)}
                  </td>
                  <td className="bar-col">
                    {/* A bar, because five numbers in a column are read one at a time and five
                        bars are read at once. ⚠ Hatched and grey when the calculator refused to
                        conclude — a solid red bar IS a conclusion. */}
                    <span className="mas-bar">
                      <span className={`mas-bar-fill tone-bg-${tone}`}
                        style={{ width: `${Math.max(2, Math.min(100, u.percent))}%` }} />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(report.typology.length > 0 || report.difficulty.length > 0) && (
        <div className="card mas-slices">
          <div>
            <h3>By what the question tested</h3>
            {report.typology.map((t) => (
              <Slice key={t.name} name={t.name} percent={t.percent} marks={t.marksAvailable} />
            ))}
            <p className="mas-sub">
              A class that is fine on recall and poor on competency has been taught the content and
              not the application. That is a different lesson plan, not more of the same one.
            </p>
          </div>
          <div>
            <h3>By difficulty</h3>
            {report.difficulty.map((t) => (
              <Slice key={t.name} name={t.name} percent={t.percent} marks={t.marksAvailable} />
            ))}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------- the children */}
      <div className="mas-filters">
        <button className={`btn ${only === 'all' ? 'btn-primary' : ''}`} onClick={() => setOnly('all')}>
          Everyone ({report.students.length})
        </button>
        <button className={`btn ${only === 'behind' ? 'btn-primary' : ''}`} onClick={() => setOnly('behind')}>
          Behind on a unit ({report.students.filter((s) => s.weakest.length > 0).length})
        </button>
        <button className={`btn ${only === 'skipped' ? 'btn-primary' : ''}`} onClick={() => setOnly('skipped')}>
          Left questions blank ({report.students.filter((s) => s.skipped > 0).length})
        </button>
      </div>

      <div className="mas-list">
        {shown.map((s) => (
          <Child key={s.studentId} s={s}
            open={open === s.studentId}
            onToggle={() => setOpen(open === s.studentId ? null : s.studentId)} />
        ))}
        {shown.length === 0 && (
          <div className="card" style={{ padding: 18, color: 'var(--ink-3)' }}>
            Nobody matches that filter — which is good news.
          </div>
        )}
      </div>
    </div>
  );
}

function Slice({ name, percent, marks }: { name: string; percent: number; marks: number }) {
  return (
    <div className="mas-slice">
      <span className="mas-slice-name">{name}</span>
      <span className="mas-bar">
        <span className={`mas-bar-fill tone-bg-${masteryTone(Math.round(percent))}`}
          style={{ width: `${Math.max(2, Math.min(100, percent))}%` }} />
      </span>
      <span className={`mas-slice-pct tone-${masteryTone(Math.round(percent))}`}>{Math.round(percent)}%</span>
      <span className="mas-slice-marks">{marks}m</span>
    </div>
  );
}

function Child({ s, open, onToggle }: {
  s: MasteryStudentView; open: boolean; onToggle: () => void;
}) {
  return (
    <div className={`card mas-child ${s.weakest.length > 0 ? 'is-behind' : ''}`}>
      <button className="mas-child-head" onClick={onToggle}>
        <span className={`mas-child-pct tone-${masteryTone(Math.round(s.overallPercent))}`}>
          {Math.round(s.overallPercent)}%
        </span>
        <span className="mas-child-main">
          <span className="mas-child-name">{s.fullName}</span>
          <span className="mas-child-line">{s.headline}</span>
        </span>
        <span className="mas-child-meta">
          {s.marksScored} of {s.marksAvailable}
          {/* ⚠ Always shown when non-zero. It changes what the percentage means. */}
          {s.skipped > 0 && <em> · {s.skipped} left blank</em>}
        </span>
      </button>

      {open && (
        <div className="mas-child-body">
          {s.units.map((u) => <UnitRow key={u.unitName} u={u} />)}
          {s.units.length === 0 && (
            <p className="mas-sub">Nothing recorded for this child on this paper.</p>
          )}
        </div>
      )}
    </div>
  );
}

function UnitRow({ u }: { u: MasteryUnitView }) {
  const weak = u.confidence !== 'TooLittleEvidence'
    && ((u.versusClass !== null && u.versusClass <= -15) || u.percent < 40);
  const pct = Math.round(u.percent);
  const tone = masteryTone(pct, u.confidence);

  return (
    <div className={`mas-urow ${weak ? 'is-weak' : ''} ${tone === 'none' ? 'is-unjudged' : ''}`}>
      <span className="mas-uname">{u.unitName}</span>
      <span className="mas-bar">
        <span className={`mas-bar-fill tone-bg-${tone}`}
          style={{ width: `${Math.max(2, Math.min(100, u.percent))}%` }} />
        {/* The class average as a tick on the child's own bar — the comparison that matters,
            without ever putting the child in an order. */}
        {u.classPercent !== null && (
          <span className="mas-bar-tick" style={{ left: `${Math.min(100, u.classPercent)}%` }} />
        )}
      </span>
      <span className={`mas-upct tone-${tone}`}>{pct}%</span>
      <span className="mas-uclass">
        {u.classPercent !== null ? `class ${Math.round(u.classPercent)}%` : ''}
      </span>
      <span className="mas-uev">
        {confidenceLabel(u.confidence, u.marksAvailable)}
        {u.questionsSkipped > 0 && ` · ${u.questionsSkipped} blank`}
      </span>
    </div>
  );
}

/**
 * WHAT TO SHOW WHEN NOBODY HAS SAID WHICH PAPER.
 *
 * ⚠ This screen needs a paper and the nav link cannot supply one. The old behaviour was to render
 * the load-failure card, which told a teacher something had broken when nothing had — and left
 * them with no way forward from a link the product itself had put in front of them.
 *
 * Papers are listed newest first with their status, because the useful question is "which one did
 * we just mark", not "which one is alphabetically first". A paper with no marks captured yet will
 * open to an empty report; that is honest and recoverable, whereas hiding it would leave a teacher
 * wondering where the paper they set last week went.
 */
function PaperPicker() {
  const [papers, setPapers] = useState<PaperSummary[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    paperApi.list()
      .then((p) => alive && setPapers(p))
      .catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, []);

  // ⚠ By the date the paper was SAT, not the date the row was created. A teacher looking for
  // "the one we just marked" is thinking of the examination, and a paper built in December for a
  // February test would otherwise sort above the one the class sat last week.
  const ordered = useMemo(
    () => [...(papers ?? [])].sort((a, b) =>
      (b.paperDate ?? b.createdAt ?? '').localeCompare(a.paperDate ?? a.createdAt ?? '')),
    [papers],
  );

  return (
    <div className="mas">
      <header className="mas-head">
        <h1>What the paper showed</h1>
        <p className="mas-sub">
          Choose a paper. This screen reads marks captured question by question, so it can tell you
          which chapter a class did not understand rather than which subject.
        </p>
      </header>

      {failed && (
        <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>
          Could not read the list of papers just now. Try again in a moment.
        </div>
      )}

      {!failed && papers === null && <p style={{ color: 'var(--ink-3)' }}>Looking for papers…</p>}

      {!failed && papers !== null && ordered.length === 0 && (
        <div className="card" style={{ padding: 20 }}>
          <p style={{ marginTop: 0 }}>No papers have been built yet.</p>
          <p className="mas-sub" style={{ marginBottom: 0 }}>
            Build one on the <Link to="/paper-builder">paper builder</Link>, mark it question by
            question, and this screen fills itself in.
          </p>
        </div>
      )}

      {ordered.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="mas-table" style={{ minWidth: 620 }}>
            <thead>
              <tr>
                <th>Paper</th><th>Subject</th><th>Class</th><th>Sat on</th>
                <th className="num">Marks</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((p) => (
                <tr key={p.id}>
                  <td style={{ paddingLeft: 14 }}>
                    {/* ⚠ Explicitly styled. Inside a table cell the link inherited the cell's ink
                        and rendered as plain black text — a list of things you are asked to choose
                        from, with nothing to say any of them can be clicked. */}
                    <Link to={`/mastery?paperId=${p.id}`}
                      style={{ color: 'var(--accent-ink)', fontWeight: 600, textDecoration: 'none' }}>
                      {p.title}
                    </Link>
                    {p.setLabel ? <span className="muted"> · Set {p.setLabel}</span> : null}
                  </td>
                  <td>{p.subjectName}</td>
                  <td>{p.classLabel ?? '—'}</td>
                  <td>{p.paperDate ? formatSatOn(p.paperDate) : '—'}</td>
                  <td className="num">{p.totalMarks}</td>
                  <td className="muted">{p.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** ⚠ Hand-parsed. `new Date('2026-01-12')` is UTC midnight and prints the previous day anywhere
    west of Greenwich; an exam date that is off by one is worse than no date. */
function formatSatOn(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}
